/**
 * Safe Date Formatting Utilities
 * 
 * Handles null/undefined dates gracefully and prevents "Invalid Date" displays.
 * All functions return fallback values instead of throwing errors.
 */

export interface DateFormatOptions {
  fallback?: string;
  includeTime?: boolean;
  format?: 'short' | 'medium' | 'long' | 'custom';
}

/**
 * Safely formats a date string/Date object to a localized date string.
 * Never returns "Invalid Date" - always returns a fallback value.
 * 
 * @param dateInput - Date string, Date object, or null/undefined
 * @param options - Formatting options
 * @returns Formatted date string or fallback
 */
export function formatDate(
  dateInput: string | Date | null | undefined,
  options: DateFormatOptions = {}
): string {
  const { fallback = '—', includeTime = false, format = 'short' } = options;

  // Handle null/undefined/empty string
  if (!dateInput) {
    return fallback;
  }

  try {
    const date = new Date(dateInput);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date input:', dateInput);
      return fallback;
    }

    if (options.format === 'custom') {
      const formatted = date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });

      // Uppercase AM/PM and remove commas for cleaner display
      return formatted.replace(/\bam\b/gi, 'AM').replace(/\bpm\b/gi, 'PM').replace(/,/g, '');
    }

    // Format options based on format type
    const formatOptions: Intl.DateTimeFormatOptions = getFormatOptions(format, includeTime);

    return date.toLocaleDateString('en-US', formatOptions);
  } catch (error) {
    console.warn('Date formatting error:', error, 'Input:', dateInput);
    return fallback;
  }
}

/**
 * Formats a date for display with time included
 */
export function formatDateTime(
  dateInput: string | Date | null | undefined,
  options: Omit<DateFormatOptions, 'includeTime'> = {}
): string {
  return formatDate(dateInput, { ...options, includeTime: true });
}

/**
 * Helper function to format time units with proper pluralization
 */
function formatTimeUnit(value: number, unit: string): string {
  const plural = value === 1 ? '' : 's';
  return `${value} ${unit}${plural} ago`;
}

/**
 * Calculates the appropriate time interval for relative time display
 */
function getRelativeTimeString(diffDays: number, diffHours: number, diffMinutes: number): string {
  if (diffMinutes < 1) {
    return 'Just now';
  } else if (diffMinutes < 60) {
    return formatTimeUnit(diffMinutes, 'minute');
  } else if (diffHours < 24) {
    return formatTimeUnit(diffHours, 'hour');
  } else if (diffDays < 7) {
    return formatTimeUnit(diffDays, 'day');
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return formatTimeUnit(weeks, 'week');
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return formatTimeUnit(months, 'month');
  } else {
    const years = Math.floor(diffDays / 365);
    return formatTimeUnit(years, 'year');
  }
}

/**
 * Formats a date as relative time (e.g., "2 hours ago", "3 days ago")
 */
export function formatRelativeTime(
  dateInput: string | Date | null | undefined,
  options: Pick<DateFormatOptions, 'fallback'> = {}
): string {
  const { fallback = '—' } = options;
  
  if (!dateInput) {
    return fallback;
  }
  
  try {
    const date = new Date(dateInput);
    
    if (isNaN(date.getTime())) {
      return fallback;
    }
    
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));

    return getRelativeTimeString(diffDays, diffHours, diffMinutes);
  } catch (error) {
    console.warn('Relative time formatting error:', error, 'Input:', dateInput);
    return fallback;
  }
}

/**
 * Validates if a date string/Date object is valid
 */
export function isValidDate(dateInput: string | Date | null | undefined): boolean {
  if (!dateInput) {
    return false;
  }
  
  try {
    const date = new Date(dateInput);
    return !isNaN(date.getTime());
  } catch {
    return false;
  }
}

/**
 * Formats date specifically for table displays (consistent short format)
 */
export function formatTableDate(
  dateInput: string | Date | null | undefined,
  fallback: string = '—'
): string {
  return formatDate(dateInput, { fallback, format: 'short' });
}

/**
 * Formats date specifically for last sync displays
 */
export function formatLastSync(
  dateInput: string | Date | null | undefined,
  fallback: string = 'Never synced'
): string {
  if (!dateInput || !isValidDate(dateInput)) {
    return fallback;
  }
  
  // For recent dates (within 24 hours), show relative time
  const date = new Date(dateInput);
  const now = new Date();
  const diffTime = now.getTime() - date.getTime();
  const diffHours = diffTime / (1000 * 60 * 60);
  
  if (diffHours < 24) {
    return formatRelativeTime(dateInput, { fallback });
  }
  
  return formatDate(dateInput, { 
    fallback, 
    format: 'custom',
    includeTime: true
  });
}

/**
 * Helper function to get format options based on format type
 */
function getFormatOptions(format: 'short' | 'medium' | 'long' | 'custom', includeTime: boolean): Intl.DateTimeFormatOptions {
  const baseOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: format === 'short' ? '2-digit' : format === 'medium' ? 'short' : 'long',
    day: '2-digit'
  };
  
  if (includeTime) {
    baseOptions.hour = '2-digit';
    baseOptions.minute = '2-digit';
    if (format === 'long') {
      baseOptions.second = '2-digit';
    }
  }
  
  return baseOptions;
}

/**
 * Safe date parser that handles common date string formats
 */
export function parseDate(dateInput: string | Date | null | undefined): Date | null {
  if (!dateInput) {
    return null;
  }
  
  if (dateInput instanceof Date) {
    return isValidDate(dateInput) ? dateInput : null;
  }

  try {
    const date = new Date(dateInput);
    return isValidDate(date) ? date : null;
  } catch {
    return null;
  }
}

/**
 * Formats a date range (start - end)
 */
export function formatDateRange(
  startDate: string | Date | null | undefined,
  endDate: string | Date | null | undefined,
  options: DateFormatOptions = {}
): string {
  const { fallback = '—' } = options;
  
  const start = formatDate(startDate, options);
  const end = formatDate(endDate, options);
  
  if (start === fallback && end === fallback) {
    return fallback;
  }
  
  if (start === fallback) {
    return `Until ${end}`;
  }
  
  if (end === fallback) {
    return `From ${start}`;
  }
  
  return `${start} - ${end}`;
}